import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Shield, ShieldCheck, X, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useMfaStatus, useMfaSetup, useMfaEnable, useMfaDisable } from '@/lib/hooks/mfa';

export function MfaCard() {
  const { data, isLoading } = useMfaStatus();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const enabled = !!data?.enabled;

  return (
    <>
      <Card variant="default" className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'grid h-10 w-10 place-items-center rounded-2 shrink-0',
                enabled ? 'bg-mint-soft text-mint-deep' : 'bg-card-soft text-fg-muted',
              )}
            >
              {enabled ? (
                <ShieldCheck className="h-5 w-5" strokeWidth={1.8} />
              ) : (
                <Shield className="h-5 w-5" strokeWidth={1.8} />
              )}
            </div>
            <div>
              <Eyebrow>Segurança</Eyebrow>
              <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
                Autenticação em dois fatores
              </h3>
              <p className="text-sm text-fg-muted mt-0.5 max-w-md">
                Um código de 6 dígitos do app autenticador (Google Authenticator, Authy, 1Password)
                além da senha.
              </p>
            </div>
          </div>
          {!isLoading && (
            <Pill tone={enabled ? 'mint' : 'default'} dot>
              {enabled ? 'ativo' : 'inativo'}
            </Pill>
          )}
        </div>

        {!isLoading && (
          <div className="flex items-center justify-end mt-4">
            {enabled ? (
              <Button variant="ghost" size="sm" onClick={() => setDisableOpen(true)}>
                Desativar
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setSetupOpen(true)}>
                Ativar 2FA
              </Button>
            )}
          </div>
        )}
      </Card>

      <SetupDialog open={setupOpen} onOpenChange={setSetupOpen} />
      <DisableDialog open={disableOpen} onOpenChange={setDisableOpen} />
    </>
  );
}

function SetupDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const setupMut = useMfaSetup();
  const enableMut = useMfaEnable();
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  async function start() {
    try {
      const r = await setupMut.mutateAsync();
      setSetup({ secret: r.secret, qrDataUrl: r.qrDataUrl });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao iniciar setup.');
    }
  }

  async function confirm() {
    if (!setup) return;
    if (!/^\d{6}$/.test(code)) {
      toast.error('Código deve ter 6 dígitos.');
      return;
    }
    try {
      await enableMut.mutateAsync({ secret: setup.secret, code });
      toast.success('2FA ativado.');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Código incorreto.');
    }
  }

  function reset() {
    setCode('');
    setSetup(null);
    setSecretCopied(false);
  }

  async function copySecret() {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(94vw,460px)] rounded-3 border border-card-stroke bg-card shadow-clay data-[state=open]:animate-fade-in">
          <header className="flex items-start justify-between gap-4 border-b border-card-stroke px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-2 bg-mint-soft text-mint-deep">
                <Shield className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div>
                <Eyebrow>Segurança</Eyebrow>
                <Dialog.Title className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg leading-[1.1] mt-0.5">
                  Ativar autenticação em 2 fatores
                </Dialog.Title>
              </div>
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-2 text-fg-muted hover:text-fg hover:bg-card-soft transition-colors"
            >
              <X className="h-4 w-4" strokeWidth={1.8} />
            </Dialog.Close>
          </header>

          <div className="px-6 py-5">
            {!setup ? (
              <>
                <ol className="space-y-2 text-sm text-fg-muted list-decimal pl-5">
                  <li>Instale Google Authenticator, Authy ou 1Password no celular.</li>
                  <li>Você vai escanear um QR code com o app.</li>
                  <li>Digite o código de 6 dígitos pra confirmar.</li>
                </ol>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full mt-5"
                  onClick={start}
                  disabled={setupMut.isPending}
                >
                  {setupMut.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                      Gerando…
                    </>
                  ) : (
                    'Começar'
                  )}
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-2 bg-white p-4 flex items-center justify-center mb-3">
                  <img src={setup.qrDataUrl} alt="QR Code 2FA" className="w-48 h-48" />
                </div>
                <p className="text-[11px] text-fg-muted text-center mb-2">
                  Não consegue escanear? Copie o código:
                </p>
                <div className="relative mb-5">
                  <input
                    readOnly
                    value={setup.secret}
                    className="w-full text-[12px] font-mono text-center px-3 py-2 pr-10 rounded-2 border border-card-stroke bg-card-soft text-fg-muted tracking-wider"
                  />
                  <button
                    type="button"
                    onClick={copySecret}
                    aria-label={secretCopied ? 'Copiado' : 'Copiar'}
                    className="absolute top-1/2 -translate-y-1/2 right-2 grid h-7 w-7 place-items-center rounded-2 text-fg-muted hover:text-mint-deep hover:bg-mint-soft transition-colors"
                  >
                    {secretCopied ? (
                      <Check className="h-3.5 w-3.5 text-mint-deep" strokeWidth={2} />
                    ) : (
                      <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
                    )}
                  </button>
                </div>
                <Label htmlFor="mfa-code" className="mb-1.5 block">
                  Código de 6 dígitos do app
                </Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center font-mono text-[18px] tracking-[0.3em]"
                  autoFocus
                />
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full mt-4"
                  onClick={confirm}
                  disabled={enableMut.isPending || code.length !== 6}
                >
                  {enableMut.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                      Confirmando…
                    </>
                  ) : (
                    'Confirmar e ativar'
                  )}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DisableDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const disableMut = useMfaDisable();
  const [code, setCode] = useState('');

  async function confirm() {
    if (!/^\d{6}$/.test(code)) {
      toast.error('Código deve ter 6 dígitos.');
      return;
    }
    try {
      await disableMut.mutateAsync({ code });
      toast.success('2FA desativado.');
      setCode('');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Código incorreto.');
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setCode('');
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(94vw,400px)] rounded-3 border border-card-stroke bg-card shadow-clay data-[state=open]:animate-fade-in">
          <header className="flex items-start justify-between gap-4 border-b border-card-stroke px-6 py-4">
            <Dialog.Title className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg leading-[1.1]">
              Desativar 2FA
            </Dialog.Title>
            <Dialog.Close
              aria-label="Fechar"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-2 text-fg-muted hover:text-fg hover:bg-card-soft transition-colors"
            >
              <X className="h-4 w-4" strokeWidth={1.8} />
            </Dialog.Close>
          </header>
          <div className="px-6 py-5">
            <p className="text-sm text-fg-muted mb-4">
              Digite o código atual do seu app autenticador pra confirmar.
            </p>
            <Input
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center font-mono text-[18px] tracking-[0.3em]"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancelar</Button>
              </Dialog.Close>
              <Button
                variant="primary"
                onClick={confirm}
                disabled={disableMut.isPending || code.length !== 6}
                className="bg-warn hover:bg-warn/90"
              >
                {disableMut.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    Desativando…
                  </>
                ) : (
                  'Desativar'
                )}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
